/**
 * Выбор состояния оплаты прямо в списке заказов.
 *
 * Раньше плашка была переключателем: одно нажатие — и заказ помечен
 * оплаченным. В журнале, где по строке ещё и открывается карточка, промах
 * пальцем менял деньги молча и без следа, а заметить это можно было только
 * при сверке с клиникой.
 *
 * Поэтому здесь тот же приём, что у статуса: нажатие ОТКРЫВАЕТ список из двух
 * пунктов, и состояние меняет уже выбор в нём. Лишнее движение стоит секунды,
 * а случайная отметка об оплате — разговора с клиникой.
 *
 * Выбор меняет ЧЕРНОВИК (§6), как и статус: в базу уходит по кнопке
 * «Сохранить изменения».
 */

import { createPortal } from 'react-dom';
import { Badge } from './ui.js';
import { usePopover } from './usePopover.js';
import { STATUS_COLORS } from '../theme.js';

const OPTIONS = [
  { paid: true, label: 'Оплачен', color: STATUS_COLORS.done },
  { paid: false, label: 'Не оплачен', color: STATUS_COLORS.danger },
] as const;

/** Ширины хватает на самую длинную подпись с запасом на палец. */
const MENU = { width: 200, height: 116 };

export function PaidQuickPicker({
  paid,
  onChange,
  disabled,
}: {
  paid: boolean;
  onChange: (paid: boolean) => void;
  disabled?: boolean;
}) {
  const popover = usePopover(MENU);

  return (
    <>
      <button
        disabled={disabled}
        title={disabled ? undefined : 'Нажмите, чтобы изменить оплату'}
        onClick={(event) => {
          // Иначе нажатие «провалится» в строку и откроет карточку заказа.
          event.stopPropagation();
          if (disabled) return;
          popover.toggle(event.currentTarget);
        }}
        className="inline-flex max-w-full items-center overflow-hidden rounded hover:opacity-75 disabled:opacity-100"
      >
        <Badge color={paid ? STATUS_COLORS.done : STATUS_COLORS.danger}>
          {paid ? 'Оплачен' : 'Не оплачен'}
        </Badge>
      </button>

      {/*
        Список рисуется В КОРНЕ СТРАНИЦЫ, а не рядом с плашкой.

        Список стоит в координатах окна (`position: fixed`), а список заказов
        показывается с масштабом. `zoom` у предка умножает координаты и
        фиксированных потомков тоже: при 80 % список уезжал вверх на пятую
        часть высоты экрана и «прилипал» далеко от кнопки, по которой его
        открыли. В корне страницы масштаба нет, и координаты совпадают
        с теми, что посчитаны по кнопке.
      */}
      {popover.open && createPortal(
        <div
          ref={popover.popoverRef}
          data-testid="paid-menu"
          role="menu"
          className="flex flex-col rounded-lg border py-1 shadow-lg"
          style={popover.style}
          onClick={(event) => event.stopPropagation()}
        >
          {OPTIONS.map((option) => (
            <button
              key={option.label}
              role="menuitemradio"
              aria-checked={option.paid === paid}
              onClick={() => {
                onChange(option.paid);
                popover.close();
              }}
              // Строки размашистые: промах на несколько точек не должен
              // попадать в соседний пункт — ошибку было бы видно не сразу.
              className="flex items-center gap-2.5 px-3 py-2.5 text-left text-sm hover:bg-black/5"
              style={{ background: option.paid === paid ? 'rgba(0,0,0,0.05)' : undefined }}
            >
              <span
                className="h-2.5 w-2.5 flex-shrink-0 rounded-full"
                style={{ background: option.color }}
              />
              <span className="text-ink">{option.label}</span>
            </button>
          ))}
        </div>,
        document.body,
      )}
    </>
  );
}
